import { styled } from '@mui/material/styles';
import { Box, Button, InputBase, Switch, Typography } from '@mui/material';

// 디자인 색상 토큰 (Figma)
// Primary/5 #4AB3BC · Gray/9 #262626 · Gray/8 #404040 · Gray/6 #737373
// Gray/4 #A6A6A6 · Gray/3 #BFBFBF · Gray/2 #D9D9D9 · Gray/1 #F2F2F2
// Sub/2 #55C982(수정) · Sub/3 #EE735A(삭제) · PDF #EF5350

// ============================================
// Page Layout
// ============================================
export const PageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  paddingTop: '16px',
});

// ============================================
// 보조 탭 (계약서 관리 / 계약서 목록)
// ============================================
export const SubTabRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

export const SubTabButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  minWidth: 'auto',
  padding: '8px 16px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  boxShadow: 'none',
  textTransform: 'none',
  ...(active
    ? {
        background: '#4AB3BC',
        color: '#fff',
        border: '1px solid #4AB3BC',
        '&:hover': { background: '#4AB3BC', boxShadow: 'none' },
      }
    : {
        background: '#fff',
        color: '#404040',
        border: '1px solid #D9D9D9',
        '&:hover': { background: '#fff', boxShadow: 'none' },
      }),
}));

// ============================================
// 검색/액션 영역
// ============================================
export const ToolbarRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  flexWrap: 'wrap',
});

export const PageSizeGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

export const PageSizeLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
  whiteSpace: 'nowrap',
});

export const SearchField = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  flex: 1,
  minWidth: '280px',
  maxWidth: '480px',
  height: '48px',
  padding: '12px 16px',
  background: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
});

export const SearchInput = styled(InputBase)({
  flex: 1,
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  '& input::placeholder': {
    color: '#A6A6A6',
    opacity: 1,
  },
});

export const ActionGroup = styled(Box)({
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

// 공통 액션 버튼 베이스 — variant 로 색상만 분기
export const ActionButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  height: '48px',
  padding: '8px 16px',
  gap: '8px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { boxShadow: 'none' },
  ...(tone === 'outline' && {
    background: '#fff',
    color: '#333',
    border: '1px solid #333',
    '&:hover': { background: '#fff' },
  }),
  ...(tone === 'danger' && {
    background: '#fff',
    color: '#EF5350',
    border: '1px solid #EF5350',
    '&:hover': { background: '#fff' },
  }),
  ...(tone === 'primary' && {
    background: '#4AB3BC',
    color: '#fff',
    border: '1px solid #4AB3BC',
    '&:hover': { background: '#4AB3BC' },
  }),
}));

// ============================================
// 표
// ============================================
export const Table = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  overflowX: 'auto',
});

export const TableHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  minWidth: 'max-content',
  background: '#F2F2F2',
  borderBottom: '1px solid #D9D9D9',
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  paddingLeft: '16px',
});

export const TableRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  minWidth: 'max-content',
  minHeight: '60px',
  background: '#fff',
  borderBottom: '1px solid #D9D9D9',
  paddingLeft: '16px',
  '&:last-of-type': { borderBottom: 'none' },
});

// 셀 — width prop 으로 고정폭, 미지정 시 flex:1
export const Cell = styled(Box, {
  shouldForwardProp: prop => prop !== 'width',
})(({ width }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px',
  ...(width ? { width: `${width}px`, flexShrink: 0 } : { flex: 1, minWidth: 0 }),
}));

export const HeaderText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#262626',
  whiteSpace: 'nowrap',
});

export const CellDivider = styled(Box)({
  width: '1px',
  height: '20px',
  background: '#BFBFBF',
  flexShrink: 0,
});

export const DateText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
  textAlign: 'center',
});

export const NameText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const ContentText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
  lineHeight: 1.3,
});

export const CategoryText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#262626',
  textAlign: 'center',
});

export const RegisterText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
  textAlign: 'center',
});

// 사용여부 토글 — 40x20, on=teal
export const UsageToggle = styled(Switch)({
  width: 40,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4AB3BC',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    backgroundColor: '#D9D9D9',
    opacity: 1,
  },
});

// 수정 / 삭제 버튼
export const RowActionGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'center',
});

export const EditButton = styled(Button)({
  minWidth: 'auto',
  height: '32px',
  padding: '6px 16px',
  borderRadius: '4px',
  border: '1px solid #D9D9D9',
  background: '#fff',
  color: '#55C982',
  fontSize: '14px',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { background: '#fff', boxShadow: 'none' },
});

export const DeleteButton = styled(Button)({
  minWidth: 'auto',
  height: '32px',
  padding: '6px 16px',
  borderRadius: '4px',
  border: '1px solid #D9D9D9',
  background: '#fff',
  color: '#EE735A',
  fontSize: '14px',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { background: '#fff', boxShadow: 'none' },
});

// 체크박스 — checked 면 teal 채움(+체크아이콘), clickable 이면 커서 포인터
export const CheckBoxCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked' && prop !== 'clickable',
})(({ checked, clickable }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  flexShrink: 0,
  borderRadius: '2px',
  border: `1.5px solid ${checked ? '#4AB3BC' : '#D9D9D9'}`,
  background: checked ? '#4AB3BC' : '#fff',
  color: '#fff',
  cursor: clickable ? 'pointer' : 'default',
}));

// 계약서 목록 — 전화번호 텍스트
export const PhoneText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
  textAlign: 'center',
});

// 계약서 목록 — 상세보기 버튼 (검정 테두리)
export const DetailButton = styled(Button)({
  minWidth: 'auto',
  height: '32px',
  padding: '6px 16px',
  borderRadius: '4px',
  border: '1px solid #333',
  background: '#fff',
  color: '#333',
  fontSize: '14px',
  fontWeight: 500,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { background: '#fff', boxShadow: 'none' },
});

// 계약서 목록 — 상태 텍스트 (완료=teal, 취소=red)
export const StatusText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'canceled',
})(({ canceled }) => ({
  fontSize: '16px',
  fontWeight: 600,
  textAlign: 'center',
  color: canceled ? '#EE735A' : '#4AB3BC',
}));
